import { useId } from 'react';

import { PersonAvatar, PersonFaceSource, initials } from 'components/person-avatar';
import { DeletedTask } from 'services/dashboard.service';
import { deletedTitle, deletionReason, formatDeletedAt, formatRegisteredAt } from 'pages/MissaoDestaque/deleted-tasks';

import { ExcluidasDoCiclo } from '../useRealizacoesDoSemestre';

/** Quem exclui é o próprio cadastrante: o cabeçalho de cada item usa os dados do usuário logado. */
export interface AutorDasExcluidas {
  chave: string;
  nomeGuerra?: string;
  rosto: PersonFaceSource;
}

interface Props {
  excluidas: ExcluidasDoCiclo | undefined;
  autor: AutorDasExcluidas;
}

const ROTULO = 'mb-1 text-[11px] text-content-soft';

function ItemExcluido({ realizacao, autor }: { realizacao: DeletedTask; autor: AutorDasExcluidas }) {
  const tituloId = useId();
  const motivo = deletionReason(realizacao);
  const nomeGuerra = autor.nomeGuerra?.trim() ?? '';

  return (
    <article aria-labelledby={tituloId} className="rounded-jj-lg border border-surface-200 bg-surface-100 px-[26px] py-5">
      <div className="flex flex-wrap items-center gap-3 border-b border-surface-200 pb-4">
        <span aria-hidden="true" className="opacity-60">
          <PersonAvatar
            {...autor.rosto}
            initials={initials(nomeGuerra)}
            size={36}
            initialsClassName="bg-brand-green font-display text-xs font-bold text-brand-navy"
          />
        </span>
        <div className="min-w-0 flex-1">
          <p className="m-0 text-[13px] text-content-soft">
            <strong className="font-bold">{nomeGuerra.split(/\s+/)[0]}</strong> {`(${autor.chave}) cadastrou`}
          </p>
          <p className="m-0 mt-1 text-[11.5px] text-content-soft">{formatRegisteredAt(realizacao.created)}</p>
        </div>
        <span className="rounded-jj-sm border border-content-soft px-2 py-1 text-[11px] font-bold uppercase leading-none tracking-[0.04em] text-content-soft">
          Excluída
        </span>
      </div>

      <dl className="m-0 mt-4 grid gap-x-[26px] gap-y-4 sm:grid-cols-[minmax(0,11rem)_minmax(0,1fr)]">
        <div className="sm:col-span-2">
          <dt className={ROTULO}>Título</dt>
          <dd className="m-0">
            {/* `leading-[normal]`: o rsuite.min.css global põe `h3 { line-height: 42px }` e o título desceria. */}
            <h3 id={tituloId} className="m-0 leading-[normal] break-words text-[13px] font-bold text-content-soft">
              {deletedTitle(realizacao)}
            </h3>
          </dd>
        </div>
        <div>
          <dt className={ROTULO}>Excluída em</dt>
          <dd className="m-0 text-[13px] text-content-soft">{formatDeletedAt(realizacao.deletion?.time)}</dd>
        </div>
        {motivo && (
          <div>
            <dt className={ROTULO}>Motivo</dt>
            <dd className="m-0 whitespace-pre-line break-words text-[13px] text-content-soft">{motivo}</dd>
          </div>
        )}
      </dl>
    </article>
  );
}

/**
 * Realizações que o próprio usuário excluiu no ciclo, abaixo da lista (RDD-11 a RDD-14). Só leitura, em tom atenuado
 * para não se confundir com o cartão das cadastradas. Some enquanto carrega e quando não há excluídas.
 */
export default function SecaoDeExcluidas({ excluidas, autor }: Props) {
  const tituloId = useId();

  if (!excluidas || (!excluidas.falhou && !excluidas.itens.length)) return null;

  return (
    <section aria-labelledby={tituloId} className="mt-8">
      <h2 id={tituloId} className="m-0 mb-3.5 font-display text-base font-bold text-brand-blue-dark">
        Realizações Destaque excluídas
      </h2>
      {excluidas.falhou ? (
        <p className="m-0 rounded-jj-lg bg-brand-white px-[26px] py-6 text-[13px] text-content shadow-jj-01">
          Não foi possível carregar as realizações excluídas.
        </p>
      ) : (
        <div className="flex flex-col gap-3 rounded-jj-lg bg-brand-white px-[26px] py-6 shadow-jj-01">
          {excluidas.itens.map(realizacao => (
            <ItemExcluido key={realizacao._id} realizacao={realizacao} autor={autor} />
          ))}
        </div>
      )}
    </section>
  );
}
